'use client';

/**
 * ReasoningBlock — collapsed-by-default disclosure for model reasoning parts.
 * Shows a shimmer-free "Thinking…" label while streaming; after that a quiet
 * "Thought process" row the user can expand. Collapsed by default is the
 * industry norm (Claude, ChatGPT) — reasoning is auditable, not ambient.
 */

import React, { useState } from 'react';
import { BrainIcon, ChevronDownIcon, Loader2Icon } from 'lucide-react';

export interface ReasoningBlockProps {
  text: string;
  isStreaming?: boolean;
}

export function ReasoningBlock({ text, isStreaming = false }: ReasoningBlockProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="mt-3 rounded-lg border border-border/60 overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full min-h-8 flex items-center gap-2 px-3 py-1.5 hover:bg-muted/50 transition-colors text-left"
        aria-expanded={isOpen}
      >
        {isStreaming ? (
          <Loader2Icon className="size-3.5 animate-spin text-muted-foreground" aria-hidden />
        ) : (
          <BrainIcon className="size-3.5 text-muted-foreground" aria-hidden />
        )}
        <span className="text-xs text-muted-foreground font-medium">
          {isStreaming ? 'Thinking…' : 'Thought process'}
        </span>
        <ChevronDownIcon
          className={`ml-auto size-4 text-muted-foreground transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      {isOpen && (
        <div className="border-t border-border/60 px-3 py-2 text-xs text-muted-foreground whitespace-pre-wrap break-words">
          {text}
        </div>
      )}
    </div>
  );
}
